import { ZyFeedbackButton } from '@/components/common/zy-feedback-button';
import { ZyProfileSidebarItem } from '@/components/common/zy-profile-sidebar-item';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarTrigger,
    useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { BookSidebarNav } from '@/nav/book-sidebar-nav';
import { getSandboxDemos } from '@/nav/sandbox-demos';
import { usePlatformSidebarOutlet } from '@/providers/platform-sidebar-outlet';
import authService from '@/services/zybook-auth-service';
import {
    BookOpen,
    ChevronRight,
    FileText,
    GitCompare,
    Layers,
    LayoutDashboard,
    Search,
    Shield,
    Wrench,
    type LucideIcon,
} from 'lucide-react';
import { forwardRef, useCallback, useMemo, useState, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';

const submenuContentClassName = 'border-sidebar-border border-l pl-[1.375rem]';

const sidebarSectionTriggerClassName = cn(
    'text-sidebar-foreground/70 ring-sidebar-ring flex h-8 w-full shrink-0 items-center justify-between gap-2 rounded-md px-2 text-left text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0',
    'data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground',
    'group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! group-data-[collapsible=icon]:justify-center cursor-pointer',
);

const sidebarNavRowClassName = 'flex h-8 w-full min-w-0 items-center justify-between gap-2 pl-0 pr-2';

const sidebarNavChevronTriggerClassName = cn(
    'text-sidebar-foreground ring-sidebar-ring flex shrink-0 cursor-pointer items-center justify-center rounded-md outline-hidden focus-visible:ring-2',
    'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
);

const sidebarNavChevronClassName = 'size-4 shrink-0 transition-transform';

type SidebarSectionTriggerProps = ComponentPropsWithoutRef<'button'> & {
    title: string;
    icon: LucideIcon;
    isActive: boolean;
    isOpen: boolean;
    children: ReactNode;
};

const SidebarSectionTrigger = forwardRef<HTMLButtonElement, SidebarSectionTriggerProps>(
    ({ title, icon: Icon, isActive, isOpen, children, className, ...props }, ref) => {
        return (
            <button
                ref={ref}
                type="button"
                title={title}
                data-active={isActive}
                className={cn(sidebarSectionTriggerClassName, className)}
                {...props}
            >
                <span className="flex min-w-0 flex-1 items-center gap-2 group-data-[collapsible=icon]:hidden">
                    <Icon
                        className="size-4 shrink-0"
                        aria-hidden
                    />
                    <span className="min-w-0 truncate">{children}</span>
                </span>
                <Icon
                    className="hidden size-4 shrink-0 group-data-[collapsible=icon]:block"
                    aria-hidden
                />
                <ChevronRight
                    className={cn(sidebarNavChevronClassName, isOpen && 'rotate-90', 'group-data-[collapsible=icon]:hidden')}
                    aria-hidden
                />
            </button>
        );
    },
);

SidebarSectionTrigger.displayName = 'SidebarSectionTrigger';

export function PlatformNavSidebar() {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const { demoRegistration, bookRegistration } = usePlatformSidebarOutlet();
    const fullAccess = authService.canSeeAllMAContent();
    const showAdminNav = authService.canAccessAdmin();
    const demos = useMemo(() => getSandboxDemos(), []);
    const [demosGroupOpen, setDemosGroupOpen] = useState(() => localStorage.getItem('demosGroupOpen') === 'true');
    const [demoSubOpen, setDemoSubOpen] = useState<Record<string, boolean>>(() => JSON.parse(localStorage.getItem('demoSubOpen') || '{}'));
    const [sandboxToolsGroupOpen, setSandboxToolsGroupOpen] = useState(() => localStorage.getItem('sandboxToolsGroupOpen') === 'true');

    const pathname = location.pathname;
    const routeDemoId = typeof params.demoId === 'string' ? params.demoId : undefined;
    const routeBookName = typeof params.bookName === 'string' ? decodeURIComponent(params.bookName) : undefined;
    const isSectionRoute = /^\/books\/[^/]+\/[^/]+/.test(pathname);
    const { state, open, hoverExpanded } = useSidebar();
    const showNestedNav = open || hoverExpanded;
    const showBookSidebarNav =
        showNestedNav &&
        isSectionRoute &&
        bookRegistration != null &&
        (routeBookName == null || bookRegistration.book.id === routeBookName);
    const isSandboxToolPath = pathname === '/demo/converter' || pathname === '/demo/batch-diff' || pathname === '/demo/document-compare';
    const isDemosSectionPath = pathname === '/demo' || pathname === '/demo/' || (pathname.startsWith('/demo/') && !isSandboxToolPath);

    // TODO: Add Documents back in when we have a way to ingest all their chapters/sections
    // const isDocumentsList = pathname === '/documents';
    // const isDocumentDetail = /^\/documents\/[^/]+$/.test(pathname);
    // const isDocumentImages = /^\/documents\/[^/]+\/[^/]+\/images$/.test(pathname);
    // const documentsActive = isDocumentsList || isDocumentDetail || isDocumentImages;

    const setDemoExpanded = useCallback((id: string, open: boolean) => {
        setDemoSubOpen(prev => ({ ...prev, [id]: open }));
    }, []);

    return (
        <Sidebar collapsible="icon">
            <SidebarHeader className="border-b">
                <div className="flex items-center gap-2">
                    <SidebarTrigger
                        className="size-8 p-2 shrink-0"
                        title={state === 'collapsed' ? 'Expand sidebar' : 'Collapse sidebar'}
                    />
                    {state !== 'collapsed' && <span className="font-semibold">Modern Authoring</span>}
                </div>
            </SidebarHeader>
            <SidebarContent>
                {fullAccess && (
                    <SidebarGroup>
                        <SidebarGroupContent>
                            <SidebarMenu>
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        asChild
                                        isActive={pathname === '/books' || pathname.startsWith('/books/')}
                                        tooltip="Books"
                                    >
                                        <Link to="/books">
                                            <BookOpen className="h-4 w-4" />
                                            <span>Books</span>
                                        </Link>
                                    </SidebarMenuButton>
                                    {showBookSidebarNav && bookRegistration && (
                                        <BookSidebarNav
                                            registration={bookRegistration}
                                            showNestedNav={showNestedNav}
                                        />
                                    )}
                                </SidebarMenuItem>
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        asChild
                                        isActive={pathname === '/subjects' || pathname.startsWith('/subjects/')}
                                        tooltip="Subjects"
                                    >
                                        <Link to="/subjects">
                                            <Layers className="h-4 w-4" />
                                            <span>Subjects</span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        asChild
                                        isActive={pathname.startsWith('/editor-dev')}
                                        tooltip="Editor dev"
                                    >
                                        <Link to="/editor-dev">
                                            <LayoutDashboard className="h-4 w-4" />
                                            <span>Editor dev</span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                                {/* TODO: Add Documents back in when we have a way to ingest all their chapters/sections
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        asChild
                                        isActive={documentsActive}
                                        tooltip="Documents and document images (/documents/…)"
                                    >
                                        <Link to="/documents">
                                            <Files className="h-4 w-4" />
                                            <span>Documents</span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem> */}
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        asChild
                                        isActive={pathname.startsWith('/search')}
                                        tooltip="Tag and book search"
                                    >
                                        <Link to="/search">
                                            <Search className="h-4 w-4" />
                                            <span>Tag &amp; book search</span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        asChild
                                        isActive={pathname === '/compare-books'}
                                        tooltip="Compare books"
                                    >
                                        <Link to="/compare-books">
                                            <GitCompare className="h-4 w-4" />
                                            <span>Compare books</span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                                {showAdminNav && (
                                    <SidebarMenuItem>
                                        <SidebarMenuButton
                                            asChild
                                            isActive={pathname === '/admin' || pathname.startsWith('/admin/')}
                                            tooltip="Admin"
                                        >
                                            <Link to="/admin">
                                                <Shield className="h-4 w-4" />
                                                <span>Admin</span>
                                            </Link>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                )}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                )}

                {fullAccess && (
                    <SidebarGroup>
                        <Collapsible
                            open={sandboxToolsGroupOpen}
                            onOpenChange={open => {
                                setSandboxToolsGroupOpen(open);
                                localStorage.setItem('sandboxToolsGroupOpen', open.toString());
                            }}
                        >
                            <CollapsibleTrigger asChild>
                                <SidebarSectionTrigger
                                    title="Sandbox tools"
                                    icon={Wrench}
                                    isActive={!showNestedNav && isSandboxToolPath}
                                    isOpen={sandboxToolsGroupOpen}
                                >
                                    Sandbox tools
                                </SidebarSectionTrigger>
                            </CollapsibleTrigger>
                            <CollapsibleContent className={cn(!showNestedNav && 'hidden')}>
                                <SidebarGroupContent className={submenuContentClassName}>
                                    <SidebarMenu>
                                        <SidebarMenuItem>
                                            <SidebarMenuButton
                                                className="cursor-pointer"
                                                variant={pathname === '/demo/converter' ? 'outline' : 'default'}
                                                isActive={pathname === '/demo/converter'}
                                                tooltip="zyXML converter: outputs zyXml_to_json section (parsed). Not the same as ProseMirror export (convertProseMirrorToZybooksJSON)."
                                                onClick={() => navigate('/demo/converter')}
                                            >
                                                <span>zyXML Converter</span>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                        <SidebarMenuItem>
                                            <SidebarMenuButton
                                                className="cursor-pointer"
                                                variant={pathname === '/demo/batch-diff' ? 'outline' : 'default'}
                                                isActive={pathname === '/demo/batch-diff'}
                                                tooltip="Batch diff"
                                                onClick={() => navigate('/demo/batch-diff')}
                                            >
                                                <span>Batch diff</span>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                        <SidebarMenuItem>
                                            <SidebarMenuButton
                                                className="cursor-pointer"
                                                variant={pathname === '/demo/document-compare' ? 'outline' : 'default'}
                                                isActive={pathname === '/demo/document-compare'}
                                                tooltip="Compare two ProseMirror JSON documents by UniqueID"
                                                onClick={() => navigate('/demo/document-compare')}
                                            >
                                                <span>Document compare</span>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    </SidebarMenu>
                                </SidebarGroupContent>
                            </CollapsibleContent>
                        </Collapsible>
                    </SidebarGroup>
                )}

                <SidebarGroup className="group/demos-scroll relative">
                    <Collapsible
                        open={demosGroupOpen}
                        onOpenChange={open => {
                            setDemosGroupOpen(open);
                            localStorage.setItem('demosGroupOpen', open.toString());
                        }}
                        className="group/collapsible"
                    >
                        <CollapsibleTrigger asChild>
                            <SidebarSectionTrigger
                                title="Expand or collapse demos list"
                                icon={FileText}
                                isActive={!showNestedNav && isDemosSectionPath}
                                isOpen={demosGroupOpen}
                            >
                                Demos
                            </SidebarSectionTrigger>
                        </CollapsibleTrigger>
                        <CollapsibleContent className={cn(!showNestedNav && 'hidden')}>
                            <SidebarGroupContent className={submenuContentClassName}>
                                <SidebarMenu>
                                    {demos.map(demo => {
                                        const isDemoIndex = (pathname === '/demo' || pathname === '/demo/') && !routeDemoId;
                                        const isActiveDemo = routeDemoId === demo.id || (isDemoIndex && demos[0]?.id === demo.id);
                                        const headingsForDemo =
                                            routeDemoId === demo.id && demoRegistration?.demoId === demo.id
                                                ? demoRegistration.headings
                                                : [];
                                        const hasOutlineNav = headingsForDemo.length > 0;
                                        const subOpen = demoSubOpen[demo.id] ?? isActiveDemo;

                                        return (
                                            <SidebarMenuItem key={demo.id}>
                                                {hasOutlineNav ? (
                                                    <Collapsible
                                                        open={subOpen}
                                                        onOpenChange={open => setDemoExpanded(demo.id, open)}
                                                    >
                                                        <div className={sidebarNavRowClassName}>
                                                            <SidebarMenuButton
                                                                className="min-w-0 flex-1 !w-auto cursor-pointer"
                                                                variant={isActiveDemo ? 'outline' : 'default'}
                                                                isActive={isActiveDemo}
                                                                tooltip={demo.name}
                                                                onClick={() => navigate(`/demo/${demo.id}`)}
                                                            >
                                                                <span
                                                                    className="truncate"
                                                                    title={demo.name}
                                                                >
                                                                    {demo.name}
                                                                </span>
                                                            </SidebarMenuButton>
                                                            <CollapsibleTrigger asChild>
                                                                <button
                                                                    type="button"
                                                                    title="Headings in this demo"
                                                                    className={sidebarNavChevronTriggerClassName}
                                                                    onClick={e => e.stopPropagation()}
                                                                >
                                                                    <ChevronRight
                                                                        className={cn(sidebarNavChevronClassName, subOpen && 'rotate-90')}
                                                                        aria-hidden
                                                                    />
                                                                </button>
                                                            </CollapsibleTrigger>
                                                        </div>
                                                        <CollapsibleContent>
                                                            <SidebarMenuSub>
                                                                {headingsForDemo.map((h, index) => (
                                                                    <SidebarMenuSubItem key={h.id ?? `heading-${index}`}>
                                                                        <SidebarMenuSubButton
                                                                            size="sm"
                                                                            href="#"
                                                                            onClick={e => {
                                                                                e.preventDefault();
                                                                                if (demoRegistration?.demoId === demo.id) {
                                                                                    demoRegistration.scrollToHeading(h.id, index);
                                                                                }
                                                                            }}
                                                                            style={{
                                                                                paddingLeft: Math.min(5 + (h.level - 1) * 5, 20),
                                                                            }}
                                                                        >
                                                                            <span
                                                                                className="truncate"
                                                                                title={h.title}
                                                                            >
                                                                                {h.title}
                                                                            </span>
                                                                        </SidebarMenuSubButton>
                                                                    </SidebarMenuSubItem>
                                                                ))}
                                                            </SidebarMenuSub>
                                                        </CollapsibleContent>
                                                    </Collapsible>
                                                ) : (
                                                    <SidebarMenuButton
                                                        className="min-w-0 w-full cursor-pointer"
                                                        variant={isActiveDemo ? 'outline' : 'default'}
                                                        isActive={isActiveDemo}
                                                        tooltip={demo.name}
                                                        onClick={() => navigate(`/demo/${demo.id}`)}
                                                    >
                                                        <span
                                                            className="truncate"
                                                            title={demo.name}
                                                        >
                                                            {demo.name}
                                                        </span>
                                                    </SidebarMenuButton>
                                                )}
                                            </SidebarMenuItem>
                                        );
                                    })}
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </CollapsibleContent>
                    </Collapsible>
                </SidebarGroup>
            </SidebarContent>
            <SidebarFooter className="border-t">
                <SidebarMenu>
                    <ZyFeedbackButton />
                    <ZyProfileSidebarItem />
                </SidebarMenu>
            </SidebarFooter>
        </Sidebar>
    );
}
